import type { ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { XClose, AlertCircle } from '@kula-ai/icons-core';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

type DocTab = { id?: number; filename: string };
type AttachmentTab = { id: number; filename: string; url: string };

// Remove "x" that only appears on row hover. When disabled (last remaining document) it stays
// dimmed and shows a tooltip instead of removing, since an offer must keep at least one document.
const RowRemove = ({
  disabled,
  disabledReason,
  onRemove,
}: {
  disabled?: boolean;
  disabledReason?: string;
  onRemove: () => void;
}) => {
  const button = (
    <Box
      as="button"
      type="button"
      display="flex"
      alignItems="center"
      flexShrink={0}
      opacity={0}
      _groupHover={{ opacity: 1 }}
      color={disabled ? 'secondary-300' : 'secondary-600'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onRemove();
      }}
    >
      <XClose size={16} />
    </Box>
  );

  if (!disabled || !disabledReason) return button;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="top">{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Left-rail file tabs for a multi-doc offer template (Figma: 198px col, 36px rows, 4px gap).
// Template docs (selectable token tabs) render first, then offer attachments added via
// "Add more files" (filename + remove, no tokens), then the footer button.
export const DocumentTabs = ({
  docs,
  selectedIndex,
  onSelect,
  onRemoveDocument,
  attachments = [],
  onRemoveAttachment,
  selectedAttachmentId,
  onSelectAttachment,
  footer,
  docErrors,
}: {
  docs: DocTab[];
  selectedIndex: number;
  onSelect: (_index: number) => void;
  onRemoveDocument?: (_index: number) => void;
  attachments?: AttachmentTab[];
  onRemoveAttachment?: (_id: number) => void;
  selectedAttachmentId?: number | null;
  onSelectAttachment?: (_id: number) => void;
  footer?: ReactNode;
  docErrors?: (string | undefined)[];
}) => (
  <Flex
    flexDir="column"
    w="198px"
    flexShrink={0}
    gap="4px"
    pr="8px"
    borderRight="1px solid"
    borderColor="border-color"
  >
    {docs.map((doc, index) => {
      const selected = index === selectedIndex;
      return (
        <Flex
          key={doc.id ?? doc.filename}
          role="group"
          align="center"
          gap="6px"
          h="36px"
          px="12px"
          borderRadius="6px"
          cursor="pointer"
          bg={selected ? 'primary-25' : 'transparent'}
          _hover={{ bg: selected ? 'primary-25' : 'secondary-25' }}
          onClick={() => onSelect(index)}
        >
          <Box flex="1" overflow="hidden">
            <TruncatedTextWithTooltip
              text={doc.filename}
              enableTooltip
              // Use the themed tooltip (secondary-950, 6px radius, no arrow) instead of
              // the component's hardcoded black #161616 default, matching the rest of the app.
              toolTipVariant="default"
              size="sm-medium"
              color={selected ? 'primary-600' : 'secondary-600'}
            />
          </Box>
          {docErrors?.[index] && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexShrink={0}
                    color="pastel-1-dark"
                  >
                    <AlertCircle
                      size={18}
                      className="[&_path]:!stroke-[1.5px]"
                    />
                  </Box>
                </TooltipTrigger>
                <TooltipContent side="top">{docErrors[index]}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onRemoveDocument && (
            <RowRemove
              disabled={docs.length <= 1}
              disabledReason="At least one document is required"
              onRemove={() => onRemoveDocument(index)}
            />
          )}
        </Flex>
      );
    })}
    {attachments.map((attachment) => {
      const selected = selectedAttachmentId === attachment.id;
      return (
        <Flex
          key={`attachment-${attachment.id}`}
          role="group"
          align="center"
          gap="6px"
          h="36px"
          px="12px"
          borderRadius="6px"
          cursor="pointer"
          bg={selected ? 'primary-25' : 'transparent'}
          _hover={{ bg: selected ? 'primary-25' : 'secondary-25' }}
          onClick={() => onSelectAttachment?.(attachment.id)}
        >
          <Box flex="1" overflow="hidden">
            <TruncatedTextWithTooltip
              text={attachment.filename}
              enableTooltip
              toolTipVariant="default"
              size="sm-medium"
              color={selected ? 'primary-600' : 'secondary-600'}
            />
          </Box>
          {onRemoveAttachment && (
            <RowRemove onRemove={() => onRemoveAttachment(attachment.id)} />
          )}
        </Flex>
      );
    })}
    {footer && <Box mt="4px">{footer}</Box>}
  </Flex>
);
